import Chip from "Components/atoms/chip"
import { LambdaTrackerUI, ScreenDependency } from "models/lambda-tracker"
import { formatDateTimeAmPm } from "utils/tools"

interface LambdaScreenDependenciesProps {
    screens: ScreenDependency[]
    onOpenLambda: (id: string) => void
    /** Opens the editor for a screen, or for a new one when null. Omitted = read only. */
    onEdit?: (screen: ScreenDependency | null) => void
}

// One card per screen: the Lambdas its data depends on (in run order) and how long a refresh takes.
const LambdaScreenDependencies = ({ screens, onOpenLambda, onEdit }: LambdaScreenDependenciesProps) => {
    return (
        <>
            {onEdit && (
                <div className="d-flex justify-content-end pt-3">
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => onEdit(null)}>
                        <i className="mdi mdi-plus me-1" aria-hidden />Add screen
                    </button>
                </div>
            )}
            {screens.length === 0 && <div className="text-center text-muted py-5">No screen dependencies are set up yet.</div>}
            <div className="lambda-dependencies">
                {screens.map(screen => (
                    <section key={screen.screenCode} className={`lambda-dependency-card border-${LambdaTrackerUI.chipVariant(screen.health)}`}>
                        <div className="dependency-head">
                            <div>
                                <h5>
                                    <i className={`${LambdaTrackerUI.healthIcon(screen.health)} health-${screen.health ?? "none"} me-2`} aria-hidden />
                                    {screen.screenName}
                                </h5>
                                <small className="text-muted">{screen.url} · {screen.lambdas.length} Lambdas</small>
                            </div>
                            <div className="dependency-totals">
                                <div><small>Total (one after another)</small><strong>{LambdaTrackerUI.duration(screen.totalAvgMs)}</strong></div>
                                <div><small>Slowest Lambda</small><strong>{LambdaTrackerUI.duration(screen.slowestAvgMs)}</strong></div>
                                {onEdit && (
                                    <button type="button" className="icon-button align-self-center" onClick={() => onEdit(screen)}
                                        aria-label={`Edit the dependencies of ${screen.screenName}`} title="Edit dependencies">
                                        <i className="mdi mdi-pencil-outline fs-5" aria-hidden />
                                    </button>
                                )}
                            </div>
                        </div>
                        <table className="table table-sm align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Lambda</th>
                                    <th>Status</th>
                                    <th className="num">Avg time</th>
                                    <th>Last sync</th>
                                </tr>
                            </thead>
                            <tbody>
                                {screen.lambdas.map((l, index) => (
                                    <tr key={l.id} className={l.isActive ? undefined : "inactive"}>
                                        <td className="text-muted">{index + 1}</td>
                                        <td>
                                            <button type="button" className="btn btn-link p-0" onClick={() => onOpenLambda(l.id)}>{l.displayName}</button>
                                            {l.source === "manual" && <> <Chip variant="info">Manual</Chip></>}
                                        </td>
                                        <td>
                                            <Chip variant={LambdaTrackerUI.chipVariant(l.currentHealth)}>
                                                <i className={`${LambdaTrackerUI.healthIcon(l.currentHealth)} me-1`} aria-hidden />
                                                {l.isActive ? LambdaTrackerUI.healthLabel(l.currentHealth) : "Inactive"}
                                            </Chip>
                                        </td>
                                        <td className="num">{LambdaTrackerUI.duration(l.avgMs)}</td>
                                        <td>{formatDateTimeAmPm(l.lastSync)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>
                ))}
            </div>
        </>
    )
}

export default LambdaScreenDependencies
